import { useState, useMemo, useCallback, useEffect } from "react";
import { Language } from "../../type/Translations.js";
import { EmployeeStatus } from "../../type/Employee.js";
import { toEntities, filterEntities } from "../../utils/array.js";
import { getUniqueByRoles } from "../../utils/employee/getUniqueByRoles.js";
import "lodash";
import "fast-memoize";
import "lodash/reverse.js";
import "lodash/uniqBy.js";
const inactiveStatuses = [EmployeeStatus.DISABLED, EmployeeStatus.INACTIVE, EmployeeStatus.USED_TO_BE_ACTIVE];
const useFilterEmployees = ({
  initialEmployees = [],
  language = Language.DEUTSCH,
  initialDeactivatedEmployees = false,
  initialShowWorkingEmployees = false,
  initialSelectedEmployeeIds = [],
  initialSearchedEmployeeString = ""
}) => {
  const [showDeactivatedEmployees, setShowDeactivatedEmployees] = useState(initialDeactivatedEmployees);
  const [showWorkingEmployees, setShowWorkingEmployees] = useState(initialShowWorkingEmployees);
  const [searchedEmployeeString, setSearchedEmployeeString] = useState(initialSearchedEmployeeString);
  const [selectedEmployeesEntities, setSelectedEmployeesEntities] = useState({});
  const employees = useMemo(
    () => showDeactivatedEmployees ? initialEmployees : initialEmployees.filter(({ status }) => status && !inactiveStatuses.includes(status)),
    [initialEmployees, showDeactivatedEmployees]
  );
  const filteredEmployees = useMemo(() => {
    const text = searchedEmployeeString == null ? void 0 : searchedEmployeeString.trim().toLowerCase();
    if (!text)
      return employees ?? [];
    return (employees == null ? void 0 : employees.filter(({ displayName }) => displayName == null ? void 0 : displayName.toLowerCase().includes(text))) ?? [];
  }, [searchedEmployeeString, employees]);
  const employeesWithMultipleRoles = useMemo(
    () => filteredEmployees.reduce((acc, employee) => {
      const uniqueByRoles = getUniqueByRoles(employee);
      return [
        ...acc,
        ...uniqueByRoles.map((position) => ({
          ...employee,
          role: position.role,
          level: position.level
        }))
      ];
    }, []),
    [filteredEmployees]
  );
  const employeeEntities = useMemo(
    () => toEntities(employeesWithMultipleRoles, "referenceId", false),
    [employeesWithMultipleRoles]
  );
  const employeesByRoles = useMemo(
    () => toEntities(employeesWithMultipleRoles, "role", true),
    [employeesWithMultipleRoles]
  );
  const sortedSelectedEmployees = useMemo(() => {
    const collator = new Intl.Collator(language, {
      ignorePunctuation: true
    });
    return Object.values(selectedEmployeesEntities).sort(
      (a, b) => collator.compare((a == null ? void 0 : a.displayName) ?? "", (b == null ? void 0 : b.displayName) ?? "")
    );
  }, [selectedEmployeesEntities, language]);
  const selectedEmployeeReferenceIds = useMemo(
    () => Object.keys(selectedEmployeesEntities),
    [selectedEmployeesEntities]
  );
  const filteredSelectedEmployeeReferenceIds = useMemo(
    () => filteredEmployees.map(({ referenceId }) => referenceId).filter((referenceId) => selectedEmployeeReferenceIds.some((currentId) => currentId === referenceId)),
    [selectedEmployeeReferenceIds, filteredEmployees]
  );
  const filteredSelectedEmployees = useMemo(
    () => filteredEmployees.filter(
      ({ referenceId }) => selectedEmployeeReferenceIds.some((currentId) => currentId === referenceId)
    ),
    [selectedEmployeeReferenceIds, filteredEmployees]
  );
  const sortedSelectedEmployeesEntities = useMemo(
    () => toEntities(sortedSelectedEmployees, "referenceId", false),
    [sortedSelectedEmployees]
  );
  const toggleAll = useCallback(() => {
    setSelectedEmployeesEntities(
      filteredEmployees.length === sortedSelectedEmployees.length ? {} : toEntities(filteredEmployees, "referenceId", false)
    );
  }, [sortedSelectedEmployees, filteredEmployees]);
  const clearAll = useCallback(() => {
    setSelectedEmployeesEntities({});
  }, []);
  const toggleEmployee = useCallback(
    (referenceId) => {
      const { [referenceId]: selected, ...rest } = selectedEmployeesEntities;
      const employee = employeeEntities[referenceId];
      const elementToAdd = selected ? [] : [employee];
      try {
        setSelectedEmployeesEntities({
          ...rest,
          ...toEntities(elementToAdd.flat(), "referenceId", false)
        });
      } catch (error) {
      }
    },
    [selectedEmployeesEntities, employeeEntities]
  );
  const toggleAllByRole = useCallback(
    (role) => {
      try {
        const employeesToToggle = employeesByRoles[role];
        const allSelected = employeesToToggle == null ? void 0 : employeesToToggle.every(
          ({ referenceId, role: employeeRole }) => role === employeeRole && !!selectedEmployeesEntities[referenceId]
        );
        const ids = employeesToToggle.map(({ referenceId }) => referenceId);
        const filteredEmployees2 = filterEntities(selectedEmployeesEntities, ids);
        const data = {
          ...filteredEmployees2,
          ...!allSelected && {
            ...toEntities(employeesToToggle, "referenceId", false)
          }
        };
        setSelectedEmployeesEntities(data);
      } catch (error) {
      }
    },
    [employeesByRoles, selectedEmployeesEntities]
  );
  const isRoleSelected = useCallback(
    (role) => {
      const employeesToAdd = employeesByRoles[role];
      return employeesToAdd == null ? void 0 : employeesToAdd.some(({ referenceId }) => !!selectedEmployeesEntities[referenceId]);
    },
    [employeesByRoles, selectedEmployeesEntities]
  );
  const isAllSelected = useMemo(
    () => filteredEmployees.length === filteredSelectedEmployeeReferenceIds.length,
    [filteredSelectedEmployeeReferenceIds, filteredEmployees]
  );
  const isAllEmployeesByRoleSelected = useCallback(
    (role) => {
      const employeesByRole = employeesByRoles[role];
      const selectedEmployeesByRole = sortedSelectedEmployees.filter(
        ({ positions }) => positions == null ? void 0 : positions.some((position) => position.role === role)
      );
      return !!selectedEmployeesByRole.length && (employeesByRole == null ? void 0 : employeesByRole.length) === selectedEmployeesByRole.length;
    },
    [employeesByRoles, sortedSelectedEmployees]
  );
  useEffect(() => {
    if (initialEmployees == null ? void 0 : initialEmployees.length) {
      const employeesToAdd = employees.filter(
        ({ referenceId }) => !initialSelectedEmployeeIds.length || initialSelectedEmployeeIds.includes(referenceId)
      );
      if (initialSearchedEmployeeString !== searchedEmployeeString || !initialSearchedEmployeeString) {
        setSearchedEmployeeString(initialSearchedEmployeeString ?? "");
      }
      setSelectedEmployeesEntities(toEntities(employeesToAdd, "referenceId", false));
    }
  }, [initialEmployees]);
  useEffect(() => {
    if (initialEmployees == null ? void 0 : initialEmployees.length) {
      setSelectedEmployeesEntities((previousEntities) => {
        const disabledUsers = filteredEmployees.filter(({ status }) => inactiveStatuses.includes(status));
        const previousSelectedEmployees = Object.values(previousEntities);
        const selectedWithoutDisabledEmployees = previousSelectedEmployees.filter(
          ({ status }) => !inactiveStatuses.includes(status)
        );
        return toEntities(
          showDeactivatedEmployees ? [...previousSelectedEmployees, ...disabledUsers] : selectedWithoutDisabledEmployees,
          "referenceId",
          false
        );
      });
    }
  }, [showDeactivatedEmployees]);
  return {
    selectedEmployees: sortedSelectedEmployees,
    selectedEmployeeReferenceIds,
    selectedEmployeesEntities: sortedSelectedEmployeesEntities,
    toggleEmployee,
    toggleAllByRole,
    isRoleSelected,
    toggleAll,
    clearAll,
    isAllSelected,
    isAllEmployeesByRoleSelected,
    setSearchedEmployeeString,
    searchedEmployeeString,
    filteredEmployees,
    filteredSelectedEmployees,
    filteredSelectedEmployeeReferenceIds,
    employeesByRoles,
    showDeactivatedEmployees,
    setShowDeactivatedEmployees,
    showWorkingEmployees,
    setShowWorkingEmployees
  };
};
export {
  useFilterEmployees
};
